import axios from 'axios';

import { connect } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react'


import { validTokenExists } from '../firebaseHelper';

import EmojiPickerModal from './EmojiPickerModal';
import MenuButton from './MenuButton';

import SavedBrowsingHeader from './SavedBrowsingHeader';
import SavedSelectionBar, { SavedSelectionAction } from './SavedSelectionBar';
import TrackInfoRow from './TrackInfoRow';
import TrackInfoRowsLoading from './TrackInfoRowsLoading';

import { useLocation } from 'react-router-dom';

import { addSavedList, deleteSavedList, saveTrack } from '../redux/actions/userActions';
import { Modal } from '@mui/material';


export const SavedTracks = (props) => {
    const {
        playTrack,
        onSelectionModeChange,
        onSelectionActionChange,
        selectionMode,
        contentToggle,
        onBack,
        initialListId,
        onListChange = () => {},

        // Redux.
        addSavedList,
        deleteSavedList,
        saveTrack,
        saveTrackMutationCount,
        savedLists,
        savedTrackIdToSavedListIds,
    } = props;
    const { state } = useLocation();
    const [selectedSavedListId, setSelectedSavedListId] = useState(
        () => (initialListId !== undefined ? initialListId : state?.selectedSavedListId) || null);
    const selectedListRef = useRef(selectedSavedListId);
    selectedListRef.current = selectedSavedListId;
    const [selectedTrackIds, setSelectedTrackIds] = useState(new Set());

    const [trackInfos, setTrackInfos] = useState([]);

    // For load more...
    const [lastUpdated, setLastUpdated] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const pendingRequest = useRef(null);
    const failedCursor = useRef(null);
    const loadMoreObserverRef = useRef();
    const latestRequestVersionRef = useRef(0);
    const mounted = useRef(true);
    const saveTrackMutationCountRef = useRef(saveTrackMutationCount);
    saveTrackMutationCountRef.current = saveTrackMutationCount;
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; latestRequestVersionRef.current += 1; };
    }, []);

    const resetToAll = () => {
        updateSelectedList(null);
    }

    const tryLoadMore = useCallback(async (requestListId, requestLastUpdated) => {
        if (!mounted.current) return;
        if (saveTrackMutationCountRef.current > 0) {
            latestRequestVersionRef.current += 1;
            pendingRequest.current = null;
            setLoading(true);
            return;
        }
        if (requestLastUpdated && pendingRequest.current !== null) return;
        const requestVersion = ++latestRequestVersionRef.current;
        pendingRequest.current = requestVersion;
        const isCurrent = () => mounted.current && requestVersion === latestRequestVersionRef.current;
        const postData = {};
        if (requestLastUpdated) postData.lastUpdated = requestLastUpdated;
        if (requestListId) postData.listId = requestListId;
        setLoading(true);
        setLoadError(false);
        failedCursor.current = requestLastUpdated;
        try {
            await validTokenExists();
            if (!isCurrent()) return;
            const { data } = await axios.post('/saved_tracks', postData);
            if (!isCurrent()) return;
            if (!Array.isArray(data?.trackInfos)) throw new Error('Invalid saved episodes response');
            setTrackInfos(previous => {
                const ids = new Set();
                return [...(requestLastUpdated ? previous : []), ...data.trackInfos]
                    .filter(track => !ids.has(track.id) && ids.add(track.id));
            });
            const nextCursor = data.lastUpdated || null;
            setLastUpdated(nextCursor);
            setHasMore(Boolean(nextCursor) && nextCursor !== requestLastUpdated);
        } catch {
            if (isCurrent()) setLoadError(true);
        } finally {
            if (isCurrent()) {
                pendingRequest.current = null;
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        if (saveTrackMutationCount > 0) {
            latestRequestVersionRef.current += 1;
            pendingRequest.current = null;
            setLoading(true);
            return;
        }
        // A save refreshes the current filter, including a list selected after mount.
        tryLoadMore(selectedListRef.current, null);
    }, [saveTrackMutationCount, tryLoadMore]);

    const isTrackSaved = (trackId) => {
        return savedTrackIdToSavedListIds[trackId] !== undefined;
    };

    const updateSelectedList = (savedListId) => {
        setSelectedSavedListId(savedListId);
        onListChange(savedListId);
        setSelectedTrackIds(new Set());
        setTrackInfos([]);
        setLastUpdated(null);
        setHasMore(false);
        tryLoadMore(savedListId, null);
    };

    const selectedSavedListName = savedLists.find(list => list.id === selectedSavedListId)?.name ?? "";
    const selectedCount = selectedTrackIds.size;

    useEffect(() => { if (!selectionMode) setSelectedTrackIds(new Set()); }, [selectionMode]);

    const toggleSelectionMode = () => {
        if (deleteSelectedInProgress) return;
        setSelectedTrackIds(new Set());
        onSelectionModeChange(!selectionMode);
    };

    const toggleTrackSelection = (trackId) => () => {
        if (deleteSelectedInProgress) return;
        setSelectedTrackIds((currentSelectedTrackIds) => {
            const nextSelectedTrackIds = new Set(currentSelectedTrackIds);
            if (nextSelectedTrackIds.has(trackId)) {
                nextSelectedTrackIds.delete(trackId);
            } else {
                nextSelectedTrackIds.add(trackId);
            }
            return nextSelectedTrackIds;
        });
    };

    const episodeCountText = (count) => count === 1 ? '1 episode' : `${count} episodes`;

    const menuOptions = ["Create new list", "Delete list"]
    const handleOptionSelected = (option) => {
        if (option === "Create new list") {
            // Handle creating a new list
            setShowCreateNewListEmojiPicker(true);
        } else if (option === "Delete list") {
            // Handle deleting the list
            if (selectedSavedListId === null) {
                window.alert('Unable to delete "all" list. Select a different list first.');
                return;
            }
            setDeleteListInProgress(false);
            setShowDeleteListConfirm(true);
        }
    };

    // Delete list.
    const [showDeleteListConfirm, setShowDeleteListConfirm] = useState(false);
    const [deleteListInProgress, setDeleteListInProgress] = useState(false);
    const handleDeleteList = () => {
        validTokenExists()
            .then(() => {
                setDeleteListInProgress(true);
                axios
                    .post('/saved_lists_delete', {
                        listId: selectedSavedListId,
                    })
                    .then((res) => {
                        if (res.data && res.data.success) {
                            deleteSavedList(selectedSavedListId);
                            resetToAll();            
                        } else {
                            window.alert('Unexpected error deleting list.');
                        }
                        setShowDeleteListConfirm(false);
                        setDeleteListInProgress(false);
                    })
                    .catch((err) => {
                        window.alert('Unexpected error deleting list.');
                        setShowDeleteListConfirm(false);
                        setDeleteListInProgress(false);
                    });
            })
            .catch((error) => {
                window.alert('Unexpected error deleting list.');
                setShowDeleteListConfirm(false);
                setDeleteListInProgress(false);
            });
    };

    // Remove selected episodes from Saved and every list.
    const [showDeleteSelectedConfirm, setShowDeleteSelectedConfirm] = useState(false);
    const [deleteSelectedInProgress, setDeleteSelectedInProgress] = useState(false);
    useEffect(() => {
        if (!selectionMode || deleteSelectedInProgress) return;
        const availableIds = new Set(trackInfos.map(track => track.id));
        setSelectedTrackIds(current => {
            const remaining = new Set([...current].filter(id => availableIds.has(id)));
            return remaining.size === current.size ? current : remaining;
        });
    }, [trackInfos, selectionMode, deleteSelectedInProgress]);
    useEffect(() => {
        onSelectionActionChange(deleteSelectedInProgress);
        return () => onSelectionActionChange(false);
    }, [deleteSelectedInProgress, onSelectionActionChange]);
    const confirmDeleteSelectedTracks = () => {
        if (selectedTrackIds.size === 0 || deleteSelectedInProgress) {
            return;
        }
        setShowDeleteSelectedConfirm(true);
    };

    const handleDeleteSelectedTracks = async () => {
        const trackIdsToDelete = Array.from(selectedTrackIds);
        if (trackIdsToDelete.length === 0 || deleteSelectedInProgress) {
            return;
        }

        setDeleteSelectedInProgress(true);
        setShowDeleteSelectedConfirm(false);
        // The save action reports failures and refreshes Saved after its mutations settle.
        await Promise.allSettled(trackIdsToDelete.map(trackId => saveTrack(trackId, false)));
        if (!mounted.current) return;
        setSelectedTrackIds(new Set());
        onSelectionModeChange(false);
        setDeleteSelectedInProgress(false);
    };

    // Create new list.
    const [showCreateNewListEmojiPicker, setShowCreateNewListEmojiPicker] = useState(false);
    const [createNewList, setCreateNewList] = useState(null);
    const onCreateNewListEmojiClick = (emojiInfo) => {
        setShowCreateNewListEmojiPicker(false);
        setCreateNewList(emojiInfo.emoji);
        // Handle the emoji selection for the new list
        for (let i = 0; i < savedLists.length; i++) {
            if (savedLists[i].name === emojiInfo.emoji) {
                // Already exists.
                setTimeout(() => {
                    addSavedList(emojiInfo.emoji, savedLists[i].id);
                    setCreateNewList(null);
                    resetToAll();
                }, 1000);
                return;
            }
        }

        validTokenExists()
            .then(() => {
                axios
                    .post('/saved_lists_new', {
                        name: emojiInfo.emoji,
                    })
                    .then((res) => {
                        if (res.data && res.data.id) {
                            addSavedList(emojiInfo.emoji, res.data.id);
                            resetToAll();
                        } else {
                            window.alert('Unexpected error creating list.');
                        }
                        setCreateNewList(null);
                    })
                    .catch((err) => {
                        window.alert('Unexpected error creating list.');
                        setCreateNewList(null);
                    });
            })
            .catch((e) => {
                window.alert('Unexpected error creating list.');
                setCreateNewList(null);
            });
    };

    useEffect(() => {
        const target = loadMoreObserverRef.current;
        if (!target || !hasMore || loading || loadError || showCreateNewListEmojiPicker
            || showDeleteListConfirm || showDeleteSelectedConfirm) return;
        let active = true;
        const observer = new IntersectionObserver(entries => {
            if (!active || !entries.some(entry => entry.isIntersecting)) return;
            active = false;
            observer.disconnect();
            tryLoadMore(selectedSavedListId, lastUpdated);
        }, { rootMargin: '20px' });
        observer.observe(target);
        return () => { active = false; observer.disconnect(); };
    }, [hasMore, loading, loadError, selectedSavedListId, lastUpdated, tryLoadMore,
        showCreateNewListEmojiPicker, showDeleteListConfirm, showDeleteSelectedConfirm]);

    return (
        <div className='savedTracksRoot'>
            <Modal
                open={showDeleteListConfirm}
                onClose={() => setShowDeleteListConfirm(false)}
            >
                <div className='center'>
                    <div style={{ background: 'var(--app-surface-elevated)', textAlign: 'center', width: '300px', padding: '16px 0px', borderRadius: '10px' }}>
                        <div className='heading'>{deleteListInProgress ? "Deleting List..." : "Delete List?"}</div>
                        <div className={deleteListInProgress ? 'animate__animated animate__pulse animate__infinite' : ''} style={{ padding: '16px' }}>
                            <span style={{ fontSize: '44px', verticalAlign: 'middle' }}>{selectedSavedListName}</span>
                        </div>
                        {!deleteListInProgress &&
                            <div style={{ display: 'flex', paddingTop: '16px', borderTop: '1px solid var(--app-separator)' }}>
                                <div style={{ flexGrow: 1 }}>
                                    <button style={{ fontWeight: '500', color: 'var(--app-text)', fontSize: '16px' }} onClick={() => setShowDeleteListConfirm(false)}>Cancel</button>
                                </div>
                                <div style={{ flexGrow: 1 }}>
                                    <button style={{ fontWeight: '500', color: 'var(--app-danger)', fontSize: '16px' }} onClick={handleDeleteList}>Delete</button>
                                </div>
                            </div>
                        }
                    </div>

                </div>
            </Modal>

            <Modal
                open={showDeleteSelectedConfirm}
                onClose={() => setShowDeleteSelectedConfirm(false)}
            >
                <div className='center'>
                    <div className='savedDeleteSelectedDialog'>
                        <div className='heading'>
                            {`Remove ${episodeCountText(selectedCount)}?`}
                        </div>
                        <div className='savedDeleteSelectedMessage'>
                            This will remove {episodeCountText(selectedCount)} from Saved and all lists.
                        </div>
                        <div className='savedDeleteSelectedActions'>
                            <button className='savedDeleteSelectedCancelButton' onClick={() => setShowDeleteSelectedConfirm(false)}>
                                Cancel
                            </button>
                            <button className='savedDeleteSelectedDeleteButton' disabled={selectedCount === 0} onClick={handleDeleteSelectedTracks}>
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            <EmojiPickerModal showEmojiPicker={showCreateNewListEmojiPicker} onEmojiClick={onCreateNewListEmojiClick} onClose={() => { setShowCreateNewListEmojiPicker(false) }} />

            {createNewList &&
                <div style={{ fontSize: '44px', fontWeight: '500', textAlign: 'center', background: 'var(--app-surface)', padding: '20px' }}>
                    <div className="animate__animated animate__tada animate__infinite">
                        {createNewList}
                    </div>
                </div>
            }

            <div className='savedTracksContent'>
                <SavedBrowsingHeader
                    onBack={onBack}
                    contentType='Episodes'
                    listId={selectedSavedListId}
                    listName={selectedSavedListName}
                    savedLists={savedLists}
                    onListChange={updateSelectedList}
                    selectionCount={selectionMode ? selectedCount : null}
                    controlsDisabled={deleteSelectedInProgress}
                    actions={<div className='savedTracksHeaderActions'>
                        {selectionMode ? (
                            <button type='button'
                                className='savedSelectionCancel'
                                onClick={toggleSelectionMode}
                                disabled={deleteSelectedInProgress}
                            >Cancel</button>
                        ) : (
                            <div className='savedTracksHeaderActionGroup'>
                                {trackInfos.length > 0 && (
                                    <button className='savedTracksHeaderButton' onClick={toggleSelectionMode}>
                                        Select
                                    </button>
                                )}
                                <div className='savedTracksHeaderMenuButton'>
                                    <MenuButton options={menuOptions} handleOptionSelected={handleOptionSelected} />
                                </div>
                            </div>
                        )}
                    </div>}
                >
                    {contentToggle}
                </SavedBrowsingHeader>

                {trackInfos.length === 0 && !hasMore && !loadError &&
                    (loading ?
                        <TrackInfoRowsLoading /> :
                        <div style={{ textAlign: 'center', padding: '60px'}}>
                            <div style={{ fontSize: '18px', fontWeight: '500' }}>
                                No saved episodes yet
                            </div>
                            <div style={{ width: '220px', margin: '0px auto', fontSize: '14px', color: 'var(--app-text-muted)', paddingTop: '16px' }}>
                                <span className='savedTracksEmptyInstruction'>
                                    {selectedSavedListId === null
                                        ? 'Tap an episode’s bookmark to save it.'
                                        : `Tap an episode’s bookmark, then choose ${selectedSavedListName}.`}
                                </span>
                            </div>
                        </div>
                    )
                }

                <div className='trackInfoRowsVerticalList savedEpisodesVerticalList'>
                    {trackInfos.map((trackInfo, index) => (
                        <div key={trackInfo.id || index} style={{ opacity: isTrackSaved(trackInfo.id) ? '1' : '0.3' }}>
                            <TrackInfoRow
                                trackInfo={trackInfo}
                                playTrack={playTrack}
                                editMode={false}
                                selectionMode={selectionMode}
                                selectionDisabled={deleteSelectedInProgress}
                                isSelected={selectedTrackIds.has(trackInfo.id)}
                                onSelectionToggle={toggleTrackSelection(trackInfo.id)}
                            />
                        </div>
                    ))}
                </div>

                {(hasMore || loadError) && (
                    <div ref={loadMoreObserverRef} style={{ textAlign: 'center', minHeight: '44px', padding: '12px 20px' }}>
                        {loadError ? <div role='alert'>
                            <div>Couldn't load saved episodes.</div>
                            <button type='button' onClick={() => tryLoadMore(selectedSavedListId, failedCursor.current)}>Try again</button>
                        </div> : loading && (
                            <div className="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
                        )}
                    </div>
                )}

                {selectionMode && (
                    <SavedSelectionBar>
                        <SavedSelectionAction destructive busy={deleteSelectedInProgress}
                            onClick={confirmDeleteSelectedTracks}
                            disabled={selectedCount === 0}
                        />
                    </SavedSelectionBar>
                )}
            </div>
        </div>
    );
}

SavedTracks.propTypes = {

}

SavedTracks.defaultProps = {
    onSelectionModeChange: () => {},
    onSelectionActionChange: () => {},
    selectionMode: false,
    saveTrackMutationCount: 0,
}

const mapStateToProps = (state) => ({
    saveTrackMutationCount: state.user.saveTrackMutationCount,
    savedLists: state.user.savedLists,
    savedTrackIdToSavedListIds: state.user.savedTrackIdToSavedListIds,
})

const mapActionsToProps = {
    addSavedList,
    deleteSavedList,
    saveTrack,
}

export default connect(mapStateToProps, mapActionsToProps)(SavedTracks);
